import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { SSE_EVENT_NEW_IN_APP_NOTIFICATION } from "@/constants/sse-event.constants";
import { invalidatePuzzleScoreboardCaches } from "@/helpers/invalidate-puzzle-scoreboard-caches";

/** Parsed payload from a NEW_IN_APP_NOTIFICATION SSE event. */
interface SseNotificationPayload {
    notificationId: string;
    type: "comment_reply" | "comment_like" | "new_follower" | "new_puzzle_followed";
    /** Present for comment_reply and comment_like types; used for cache invalidation. */
    playablePropertyId?: string | null;
}

/**
 * Types whose payload includes a playablePropertyId that should trigger
 * invalidation of the comments and like-counts React Query caches.
 */
const NOTIFICATION_TYPES_WITH_PLAYABLE_PROPERTY = new Set(["comment_reply", "comment_like"]);

/**
 * Establishes a user-scoped SSE connection for real-time in-app notification updates.
 *
 * On receiving a NEW_IN_APP_NOTIFICATION event:
 * 1. Always invalidates the unread notification count cache.
 * 2. For comment_reply / comment_like events with a playablePropertyId, also
 *    invalidates the comments and comment-like-counts caches so the user sees
 *    fresh data when they navigate to the puzzle from the notification.
 *
 * @param userId - The authenticated user's ID, or undefined/null when unauthenticated
 */
const useNotificationSSE = (userId: string | undefined | null): void => {
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!userId) {
            return;
        }

        const eventSource = new EventSource(`/api/events/notifications/${userId}`, {
            withCredentials: true,
        });

        eventSource.addEventListener(SSE_EVENT_NEW_IN_APP_NOTIFICATION, (event: MessageEvent<string>) => {
            try {
                const data: SseNotificationPayload = JSON.parse(event.data);

                // Always invalidate the unread count so the badge updates in real time.
                queryClient.invalidateQueries({ queryKey: ["in-app-unread-notification-count", userId] });

                // For comment-related notifications, also invalidate the comments
                // and like-counts caches so the puzzle scoreboard shows fresh data.
                if (
                    NOTIFICATION_TYPES_WITH_PLAYABLE_PROPERTY.has(data.type) &&
                    data.playablePropertyId != null
                ) {
                    invalidatePuzzleScoreboardCaches(queryClient, String(data.playablePropertyId));
                }
            } catch (parseError) {
                console.error("Failed to parse NEW_IN_APP_NOTIFICATION SSE payload", parseError);
            }
        });

        eventSource.onerror = () => {
            console.error("Notification SSE connection error");
        };

        return () => {
            eventSource.close();
        };
    }, [userId, queryClient]);
};

export default useNotificationSSE;
